import React, { useEffect, useState } from 'react';
import codes from 'country-calling-code';
import { Form, Formik, FormikHelpers, Field } from 'formik';
import { TextField, Select } from 'formik-material-ui';
import { find } from 'lodash';
import { auth } from '../../firebaseConfig';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import * as authService from '../../api/authService';
import useAuthContext from '../../hooks/useAuthContext';
import useInitContext from '../../hooks/useInitContext';
import useNavContext from '../../hooks/useNavContext';
import useDeepLinkContext from '../../hooks/useDeepLinkContext';
import Flexlogo from '../../assets/images/logo-red.png';
import ConfirmationDialog from '../../components/Dialogs/ConfirmationDialog';
import Spinner from '../../components/Spinner';

declare const window: any;

const useStyles = makeStyles((theme) => ({
  content: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(8),
    },
    [theme.breakpoints.down('sm')]: {
      margin: '0 auto',
      padding: theme.spacing(),
    },
  },
  form: {
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(),
    },
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(),
    justifyContent: 'center',
  },
  logoContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: 140,
    height: 50,
  },
  merchantLogo: {
    height: 100,
    width: 100,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  headerText: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 25,
  },
  actionButton: {
    marginTop: theme.spacing(2),
    color: '#666666',
  },
  actionsMobNum: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(),
    justifyContent: 'center',
  },
  delayText: {
    marginTop: theme.spacing(),
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
}));

interface Values {
  countryCode: string;
  firstName: string;
  lastName: string;
  mobNum: string;
}

interface PINValues {
  PIN: string;
}

const ValidationSchema = Yup.object().shape({
  countryCode: Yup.string().required('Required'),
  mobNum: Yup.string()
    .matches(/^[0-9]+$/, 'Only numbers are allowed')
    .required('Required'),
});

const ValidationPINSchema = Yup.object().shape({
  PIN: Yup.string()
    .required('Required')
    .length(6, 'Required for length for Code is 6'),
});

const initialValues: Values = {
  countryCode: 'US',
  firstName: '',
  lastName: '',
  mobNum: '',
};

const initialPINValues: PINValues = {
  PIN: '',
};

/**
 * SignUp displays the sign up page.
 */
export default function SignUpMobile() {
  const classes = useStyles();
  const authentication = useAuthContext();
  const init = useInitContext();
  const nav = useNavContext();
  const dl = useDeepLinkContext();
  const merchantLogo = dl?.deeplink?.logo;
  const [verifyingPIN, setVerifyingPIN] = useState(false);
  const [selectedMobNum, setSelectedMobNum] = useState('');
  const [mobileToken, setMobileToken] = useState('');
  const [isMobileLoginProcessing, setIsMobileLoginProcessing] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [isMobileLoginError, setIsMobileLoginError] = useState(false);
  const [isDelayPIN, setIsDelayPIN] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (isDelayPIN) {
      setTimeout(() => {
        setIsDelayPIN(false);
      }, 3000);
    }
  }, [isDelayPIN]);

  useEffect(() => {
    const handleMobileToken = async () => {
      if (mobileToken && selectedMobNum && authentication && nav) {
        try {
          const resAuth = await authService.mobileLogin(
            selectedMobNum,
            mobileToken
          );

          if (resAuth) {
            setSelectedMobNum('');
            setMobileToken('');
            setIsMobileLoginProcessing(false);
            authentication.setSession({
              id: resAuth.id,
              token: resAuth.token,
              userId: '1',
            });

            init?.setInit({
              isFirstLoginSignup: true,
            });

            nav.setNav({
              menuNav: 'home',
            });
          }
        } catch (e) {
          console.log('error: ', e);
          setAlertText('Mobile Login Error');
          setAlertOpen(true);
        }
      }
    };

    if (
      mobileToken.length > 0 &&
      selectedMobNum.length > 0 &&
      isMobileLoginProcessing
    ) {
      handleMobileToken();
    }
  }, [
    isMobileLoginProcessing,
    nav,
    authentication,
    mobileToken,
    selectedMobNum,
    init,
  ]);

  const generateRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        'recaptcha-container',
        {
          size: 'invisible',
          callback: (response: any) => {
            console.log('Response Captcha: ', response);
          },
        },
        auth
      );
    }
  };

  const handleSubmit = async (
    values: Values,
    { setFieldError, setSubmitting }: FormikHelpers<Values>
  ) => {
    const { mobNum, countryCode, firstName, lastName } = values;
    const callingCode = find(codes, function (code) {
      return code.isoCode2 === countryCode;
    });
    setIsMobileLoginProcessing(true);

    if (callingCode) {
      generateRecaptcha();
      let verifier = window.recaptchaVerifier;
      const phoneNum = '+' + callingCode.countryCodes + ' ' + mobNum.trim();
      setSelectedMobNum(phoneNum);

      try {
        // Sign Up
        await authService.signUpMobile(phoneNum, firstName, lastName);

        // Send OTP
        signInWithPhoneNumber(auth, phoneNum, verifier)
          .then((result) => {
            window.confirmationResult = result;
            setVerifyingPIN(true);
            window.recaptchaVerifier = null;
            setIsMobileLoginProcessing(false);
            setIsDelayPIN(true);
          })
          .catch((error) => {
            console.log('Error Signin Phone Number: ', error);
            setSubmitting(false);
            setAlertText('Mobile Sign Up Error');
            setAlertOpen(true);
            setIsMobileLoginError(true);
            setIsMobileLoginProcessing(false);
          });
      } catch (e: any) {
        e.data.forEach((err: any) => {
          setFieldError(err.meta.attribute, err.detail);
          if (err.detail === 'mobile number has been taken') {
            // get token for existing mobile number
            // Send OTP
            signInWithPhoneNumber(auth, phoneNum, verifier)
              .then((result) => {
                window.confirmationResult = result;
                setVerifyingPIN(true);
                window.recaptchaVerifier = null;
                setIsMobileLoginProcessing(false);
                setIsDelayPIN(true);
              })
              .catch((error) => {
                console.log('Error Signin Phone Number: ', error);
                setSubmitting(false);
                setAlertText('Mobile Sign Up Error');
                setAlertOpen(true);
                setIsMobileLoginError(true);
                setIsMobileLoginProcessing(false);
              });
          } else {
            setAlertText('Mobile Sign Up Error');
            setSubmitting(false);
            setAlertOpen(true);
            setIsMobileLoginError(true);
            setIsMobileLoginProcessing(false);
          }
        });
      }
    }
  };

  const handlePINSubmit = (
    values: PINValues,
    { setFieldError, setSubmitting }: FormikHelpers<PINValues>
  ) => {
    const { PIN } = values;
    let confirmationResult = window.confirmationResult;

    console.log('Confirmation Result: ', confirmationResult);

    confirmationResult
      .confirm(PIN)
      .then(async (result: any) => {
        console.log('result: ', result);
        if (result) {
          const user = result.user;
          try {
            const token = await user.getIdToken();
            console.log('Token: ', token);
            setMobileToken(token);
            setIsMobileLoginProcessing(true);
          } catch (e: any) {
            console.log('Error Getting Token: ', e);
            setAlertText('Mobile Login Error');
            setIsMobileLoginError(true);
            setAlertOpen(true);
            setSubmitting(false);
          }
        }
      })
      .catch((error: any) => {
        console.log('Error:', error);
        setFieldError('PIN', 'Wrong code');
        setSubmitting(false);
      });
  };

  return (
    <>
      {alertOpen && (
        <ConfirmationDialog
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          onCancel={() => setAlertOpen(false)}
          onConfirm={() => {
            if (isMobileLoginError) {
              setAlertOpen(false);
              setIsMobileLoginError(false);
              if (alertText === 'mobile number has been taken') {
                history.push('/loginMobile');
              }
            } else {
              setAlertOpen(false);
            }
          }}
          title="Mobile Sign Up"
          body={alertText}
          confirmButtonText="Ok"
        />
      )}
      <Grid container>
        <Grid item xs={12}>
          <div className={classes.content}>
            <div className={classes.logoContainer}>
              <img
                src={merchantLogo ? merchantLogo : Flexlogo}
                className={merchantLogo ? classes.merchantLogo : classes.logo}
                alt="flex reward"
              />
            </div>
            <div className={classes.header}>
              <Typography className={classes.headerText}>
                {verifyingPIN
                  ? 'Enter Verification Code'
                  : 'Sign Up with Mobile'}
              </Typography>
              {isDelayPIN && verifyingPIN && (
                <Typography className={classes.delayText}>
                  {`Your code is on it's way...`}
                </Typography>
              )}
            </div>
            {isMobileLoginProcessing && (
              <Box display="flex" justifyContent="center" minHeight="100dvh">
                <Spinner />
              </Box>
            )}
            {!verifyingPIN && !isMobileLoginProcessing && (
              <Formik
                initialValues={initialValues}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, isValid }) => (
                  <Form noValidate className={classes.form}>
                    <Grid
                      container
                      spacing={2}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Grid item xs={12}>
                        <Field
                          component={Select}
                          name="countryCode"
                          label=""
                          fullWidth
                        >
                          {codes.map((code) => (
                            <MenuItem value={code.isoCode2} key={code.isoCode2}>
                              {'+' + code.countryCodes + ' ' + code.country}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          component={TextField}
                          className={classes.textField}
                          name="mobNum"
                          fullWidth
                          variant="standard"
                          placeholder="Mobile Number*"
                          type="tel"
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Field
                      component={TextField}
                      className={classes.textField}
                      name="firstName"
                      fullWidth
                      variant="standard"
                      placeholder="First Name"
                    />

                    <Field
                      component={TextField}
                      className={classes.textField}
                      name="lastName"
                      fullWidth
                      variant="standard"
                      placeholder="Last Name"
                    />

                    <div className={classes.actions}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !isValid}
                        fullWidth
                        size="medium"
                      >
                        CREATE ACCOUNT
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        className={classes.actionButton}
                        fullWidth
                        size="medium"
                        onClick={() => history.goBack()}
                      >
                        BACK
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
            {verifyingPIN && (
              <Formik
                initialValues={initialPINValues}
                validationSchema={ValidationPINSchema}
                onSubmit={handlePINSubmit}
              >
                {({ isSubmitting, setSubmitting }) => (
                  <Form noValidate className={classes.form}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          component={TextField}
                          className={classes.textField}
                          label=""
                          name="PIN"
                          fullWidth
                          variant="standard"
                          inputProps={{
                            style: { textAlign: 'center' },
                            inputMode: 'numeric', // Show numeric keyboard on mobile
                            pattern: '[0-9]*', // Suggest only numbers
                            maxLength: 6,
                          }}
                        />
                      </Grid>
                    </Grid>
                    <div className={classes.actionsMobNum}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            color="primary"
                            size="medium"
                            fullWidth
                            disabled={isSubmitting}
                          >
                            VERIFY
                          </Button>
                          <Button
                            type="button"
                            variant="outlined"
                            className={classes.actionButton}
                            fullWidth
                            size="medium"
                            onClick={() => {
                              setVerifyingPIN(false);
                              setIsMobileLoginProcessing(false);
                            }}
                          >
                            BACK
                          </Button>
                        </Grid>
                      </Grid>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </Grid>
      </Grid>
      <div id="recaptcha-container"></div>
    </>
  );
}
